import { computed } from "mobx";
import { ChronologicalReportingEvent } from "../../workers/NetworkEventDbSearchWebWorkerStructures";
import { AllOption } from "./SingleOriginFilterState";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { SearchUiState } from "./SearchTermUiState";
import type { EventSearchResults } from "../../lib/searchNetworkEvent";
import { SearchResultsObserver } from "./SearchResultsObserver";
import { NonSearchChronologicalEventsObserver } from "../../lib/Analysis/ReportableEventsObserver";
import { ChronologicalResultsObserver } from "./ChronologicalResultsObserver";


export class FilteredChronologicalResultsObserver  implements ChronologicalResultsObserver{
	protected readonly getSearchUiState: () => SearchUiState;
	protected readonly getSearchResultsState: () => SearchResultsObserver;
	protected readonly getReportableEventsState: () => NonSearchChronologicalEventsObserver;

	constructor({getSearchUiState, getSearchResultsState, getReportableEventsState} : {
		readonly getSearchUiState: () => SearchUiState,
		readonly getSearchResultsState: () => SearchResultsObserver;
		readonly getReportableEventsState: () => NonSearchChronologicalEventsObserver;
	}) {
		this.getSearchUiState = getSearchUiState;
		this.getSearchResultsState = getSearchResultsState;
		this.getReportableEventsState = getReportableEventsState;
	}

	@computed get searchUiState() { return this.getSearchUiState(); }
	@computed get searchResultsState() { return this.getSearchResultsState(); }
	@computed get reportableEventsState() { return this.getReportableEventsState(); }

	@computed get recordingUuid() { return this.searchUiState.recordingUuid; };

	@computed get filteredSearchResultEvents() {
		const {sourcePageUrlFilter} = this.searchUiState;
		return this.searchResultsState.hasSearchResultsWithNetworkRequestInformation
			// filter events by sourcePage (tab) url and destination url.
			.filter( s => {
				// make sure the source page matches the source url filter
				if (!sourcePageUrlFilter( s.originUrl )) {
					return false;
				}
				// if this is a network event if will have a destination url, and
				// make sure it matches the destination url filter
				if (this.searchUiState.destinationUrlFilterState.isFilterAll)
					// No need to filter by destination
					return true;
				if (this.searchUiState.destinationUrlFilterState.isFilterThirdParties) {
					// Third party filtering is done here since we have knowledge of source and destination
					return getRegisteredDomain(s.destinationUrl) !== getRegisteredDomain(s.originUrl);
				}
				// Filtering of specific domains is done by the filter
				return this.searchUiState.destinationUrlFilterState.urlFilter(s.destinationUrl)
			})
			// Filter search results within each event by search term
			.map((eventWithSearchResults) => {
				// remove search results for the network event that don't match the search term filter
				const {searchResults: searchResultsToFilter, ...event} = eventWithSearchResults;
				const searchResults = searchResultsToFilter.filter((searchResult) => {
					// Filter out search results for terms that do match the term to be shown
					if (this.searchUiState.showOnlySearchTerm != null &&
						searchResult.searchTermAsSpecified !== this.searchUiState.showOnlySearchTerm
					) {
						return false;
					}
					// Filter for search results that do not match the data flow direction
					if (this.searchUiState.dataFlowDirection != AllOption &&
						searchResult.direction !== this.searchUiState.dataFlowDirection
					) {
						return false;
					}
					return true;
				});
				return { ...event, searchResults } satisfies EventSearchResults;
			})
			// Remove events where all the search results were filtered out
			.filter((event) => {
				return event.searchResults.length > 0;
		});
	}

	@computed get filteredReportableEvents() {
		const {sourcePageUrlFilter} = this.searchUiState;
		return this.reportableEventsState.reportableEvents.filter( e => sourcePageUrlFilter(e.tab.url) );
	}

	@computed get chronologicalResults(): ChronologicalReportingEvent[] {
		// Merge the two sorted lists of events with search results and other reportable events
		const searchResults = [...this.filteredSearchResultEvents];
		const otherReportableEvents = [...this.filteredReportableEvents];
		// const searchResultsLength = searchResults.length;
		// const otherReportableEventsLength = otherReportableEvents
		const results = [] as ChronologicalReportingEvent[];
		while (searchResults.length > 0 || otherReportableEvents.length > 0) {
			if (searchResults.length === 0) {
				results.push(...otherReportableEvents.map( (e, i) => ({...e, chronologicalEventIndex: results.length + i}) ));
				break
			} else if (otherReportableEvents.length === 0) {
				results.push(...searchResults.map( (e, i) => ({...e, chronologicalEventIndex: results.length + i}) ));
				break
			} else if (searchResults[0]!.whenRecordedIso < otherReportableEvents[0]!.whenRecordedIso) {
				results.push({...searchResults.shift()!, chronologicalEventIndex: results.length});
			} else {
				results.push({...otherReportableEvents.shift()!, chronologicalEventIndex: results.length});
			}
		}
		// ProcessInfo.log("filteredChronologicalResults", searchResultsLength, otherReportableEventsLength, results.length);
		return results;
	}
}
